import React, { useState } from 'react';
import './SystemDesignLifecycle.css';

const stages = [
    {
        id: 1,
        title: "1. Requirements & Feasibility",
        icon: "📋",
        color: "#3b82f6",
        items: [
            { label: "Functional", desc: "User stories, core features, API contracts." },
            { label: "Non-Functional", desc: "Latency (p99), Availability (99.99%), Scalability, Security." },
            { label: "Estimates", desc: "RPS, Storage (TB/day), Bandwidth requirements." }
        ]
    },
    {
        id: 2,
        title: "2. High-Level Architecture",
        icon: "🏗️",
        color: "#8b5cf6",
        items: [
            { label: "Comm Patterns", desc: "REST/gRPC vs. Async (Kafka/RabbitMQ) vs. Streaming." },
            { label: "Arch Styles", desc: "Monolith vs. Microservices vs. Serverless." },
            { label: "API Gateway", desc: "Routing, Auth, SSL Termination, Rate Limiting." }
        ]
    },
    {
        id: 3,
        title: "3. Detailed Design",
        icon: "🔬",
        color: "#10b981",
        groups: [
            {
                name: "Database Design",
                items: ["SQL (ACID) vs NoSQL (Base)", "Data Modeling", "CAP Theorem"]
            },
            {
                name: "Scaling & Perf",
                items: ["Caching (Redis)", "Load Balancing (L4/L7)", "CDN (Edge)"]
            },
            {
                name: "Reliability",
                items: ["Replication", "Sharding", "Circuit Breakers"]
            }
        ]
    },
    {
        id: 4,
        title: "4. Ops & Observability",
        icon: "🚀",
        color: "#f59e0b",
        items: [
            { label: "Monitoring", desc: "Metrics (Prometheus), Health Checks, Alerts." },
            { label: "Observability", desc: "Logging (ELK), Distributed Tracing (Jaeger)." },
            { label: "Deployment", desc: "CI/CD Pipelines, Blue/Green, Canary releases." }
        ]
    }
];

const crossCutting = [
    { label: "Security", desc: "AuthN/AuthZ, Encryption (TLS), Secret Management." },
    { label: "Concurrency", desc: "Distributed Locks, Pessimistic vs Optimistic Locking." },
    { label: "Consensus", desc: "Leader Election (Zookeeper/Raft), Service Discovery." }
];

export default function SystemDesignLifecycle() {
    const [activeStage, setActiveStage] = useState(1);

    return (
        <div className="sdl-container">
            <div className="sdl-header">
                <h3>The Ultimate System Design Lifecycle</h3>
                <p>A holistic framework from requirements to operations.</p>
            </div>

            <div className="sdl-layout">
                {/* Stages Navigation */}
                <div className="sdl-stages-nav">
                    {stages.map(s => (
                        <button
                            key={s.id}
                            className={`sdl-stage-btn ${activeStage === s.id ? 'active' : ''}`}
                            onClick={() => setActiveStage(s.id)}
                            style={{ '--stage-color': s.color }}
                        >
                            <span className="sdl-stage-icon">{s.icon}</span>
                            <span className="sdl-stage-title">{s.title}</span>
                        </button>
                    ))}
                </div>

                {/* Active Content */}
                <div className="sdl-content-area">
                    <div className="sdl-card" style={{ borderTop: `4px solid ${stages.find(s => s.id === activeStage).color}` }}>
                        <h4>{stages.find(s => s.id === activeStage).icon} {stages.find(s => s.id === activeStage).title}</h4>

                        {activeStage === 3 ? (
                            <div className="sdl-detailed-grid">
                                {stages[2].groups.map((g, i) => (
                                    <div key={i} className="sdl-group">
                                        <h5>{g.name}</h5>
                                        <ul>
                                            {g.items.map((item, j) => <li key={j}>{item}</li>)}
                                        </ul>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="sdl-items-list">
                                {stages.find(s => s.id === activeStage).items.map((it, i) => (
                                    <div key={i} className="sdl-item">
                                        <strong>{it.label}:</strong> {it.desc}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Cross-Cutting Sidebar */}
                    <div className="sdl-cross-cutting">
                        <h5>🛡️ Cross-Cutting Concerns</h5>
                        {crossCutting.map((cc, i) => (
                            <div key={i} className="sdl-cc-item">
                                <strong>{cc.label}</strong>
                                <span>{cc.desc}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <div className="sdl-footer">
                <span className="sdl-tag">Continuous Feedback & Iteration</span>
            </div>
        </div>
    );
}
